import { AlertCircle, ExternalLink } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import type { AdAnalysisSummary, AnalysisCount, TopAd } from '@/contracts/onboarding-session';
import { safeHttpUrl } from '@/lib/safeHttpUrl';

import {
  AD_ANALYSIS,
  analyzingLabel,
  countLabel,
  humanizeToken,
  partialAnalysisNote,
  percentLabel,
  unavailableAnalysisNote,
} from '../copy';
import type { DataState } from '../state/buildTranscript';
import { AdThumb } from './AdThumb';

const share = (part: number, total: number): number => (total > 0 ? Math.min(1, Math.max(0, part / total)) : 0);

/** One labelled bar. The numbers are always written out, so the bar never carries meaning by length or colour alone. */
function MixBar({ label, count, total }: { label: string; count: number; total: number }) {
  const fraction = share(count, total);
  return (
    <li data-testid="analysis-bar" className="text-label tabular-nums">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-foreground">{label}</span>
        <span className="text-ds-text-2">{`${count} · ${percentLabel(fraction)}`}</span>
      </div>
      <div aria-hidden="true" className="mt-1 h-1.5 overflow-hidden rounded-full bg-ds-ink-a10">
        <div className="h-full rounded-full bg-ds-ink" style={{ width: `${Math.round(fraction * 100)}%` }} />
      </div>
    </li>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={title} className="mt-3">
      <p className="mb-1 text-label text-ds-text-2">{title}</p>
      {children}
    </div>
  );
}

function Bars({ title, items }: { title: string; items: readonly { label: string; count: number }[] }) {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) return null;
  return (
    <Group title={title}>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <MixBar key={item.label} label={item.label} count={item.count} total={total} />
        ))}
      </ul>
    </Group>
  );
}

/** Patterns that can overlap (an ad can have several hooks), so counts only, no shares. */
function Patterns({ title, items }: { title: string; items: readonly AnalysisCount[] }) {
  if (items.length === 0) return null;
  return (
    <Group title={title}>
      <ul className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <li
            key={item.name}
            data-testid="analysis-pattern"
            className="rounded-md bg-ds-ink-a05 px-2 py-0.5 text-label text-foreground"
          >
            {`${humanizeToken(item.name)} · ${item.count}`}
          </li>
        ))}
      </ul>
    </Group>
  );
}

function TopAdRow({ ad, rank }: { ad: TopAd; rank: number }) {
  const href = safeHttpUrl(ad.adUrl);
  const text = ad.title?.trim() || ad.copy?.trim() || 'Untitled ad';
  return (
    <li data-testid="top-ad" className="flex items-center gap-2.5 border-t border-ds-hairline py-1.5 text-label first:border-t-0">
      <span className="w-4 shrink-0 tabular-nums text-ds-text-3" aria-hidden="true">{rank}</span>
      <AdThumb src={ad.thumbnailUrl} className="h-12 w-9" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-foreground">{text}</p>
        <p className="truncate text-ds-text-3">
          {[ad.mediaType?.trim() ? humanizeToken(ad.mediaType) : null, `${countLabel(ad.daysRunning, 'day', 'days')} running`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-ds-text-2 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-focus"
        >
          View ad
          <ExternalLink className="size-3" aria-hidden="true" />
          <span className="sr-only">{`(opens in a new tab): ${text}`}</span>
        </a>
      ) : null}
    </li>
  );
}

function TopAds({ ads }: { ads: readonly TopAd[] }) {
  if (ads.length === 0) return null;
  return (
    <Group title={AD_ANALYSIS.topAds}>
      <ol>
        {ads.map((ad, i) => (
          <TopAdRow key={ad.adId} ad={ad} rank={i + 1} />
        ))}
      </ol>
    </Group>
  );
}

/** What the profile has so far; every part is left out when it is empty or null, never filled in. */
function Findings({ analysis }: { analysis: AdAnalysisSummary }) {
  const { video, static: still } = analysis.mediaMix;
  const rate = percentLabel(analysis.longRunningAdRate);
  const average = analysis.avgDaysRunning;
  const hasAny =
    video + still > 0 ||
    analysis.funnelStages.length > 0 ||
    analysis.topHooks.length > 0 ||
    analysis.topStyles.length > 0 ||
    analysis.formats.length > 0 ||
    rate !== null ||
    average !== null;
  if (!hasAny) return <p className="text-label text-ds-text-3">{AD_ANALYSIS.noPatterns}</p>;
  return (
    <>
      <Bars
        title={AD_ANALYSIS.mediaMix}
        items={[
          { label: 'Video', count: video },
          { label: 'Static', count: still },
        ]}
      />
      <Bars
        title={AD_ANALYSIS.funnelStages}
        items={analysis.funnelStages.map((s) => ({ label: humanizeToken(s.name), count: s.count }))}
      />
      <Patterns title={AD_ANALYSIS.topHooks} items={analysis.topHooks} />
      <Patterns title={AD_ANALYSIS.topStyles} items={analysis.topStyles} />
      <Patterns title={AD_ANALYSIS.formats} items={analysis.formats} />
      {rate !== null || average !== null ? (
        <p data-testid="analysis-running" className="mt-2 text-label text-ds-text-2">
          {[
            rate !== null ? `30+ days: ${rate}` : null,
            average !== null ? `Average ${Math.round(average)} days running` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}
    </>
  );
}

function Note({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'warn' }) {
  return (
    <p data-testid="analysis-note" className={`flex items-start gap-1.5 text-label ${tone === 'warn' ? 'text-ds-text-2' : 'text-ds-text-3'}`}>
      {tone === 'warn' ? <AlertCircle className="mt-px size-3.5 shrink-0 text-ds-badge-warning-text" aria-hidden="true" /> : null}
      <span>{children}</span>
    </p>
  );
}

function Body({ analysis }: { analysis: AdAnalysisSummary }) {
  const { analyzed, analyzable } = analysis.coverage;
  switch (analysis.state) {
    case 'queued':
      return <Note>{AD_ANALYSIS.queued}</Note>;
    case 'skipped':
      return <Note>{AD_ANALYSIS.skipped}</Note>;
    case 'running':
      return (
        <div aria-live="polite" className="flex flex-col gap-2">
          <p className="flex items-center gap-2 text-body-sm text-ds-text-2">
            <Spinner size="sm" />
            <span data-testid="analysis-progress-text" className="onb-shimmer">{analyzingLabel(analyzed, analyzable)}</span>
          </p>
          {analyzable > 0 ? (
            <div
              role="progressbar"
              aria-label="Ads analyzed"
              aria-valuemin={0}
              aria-valuemax={analyzable}
              aria-valuenow={Math.min(analyzed, analyzable)}
              className="h-1.5 overflow-hidden rounded-full bg-ds-ink-a10"
            >
              <div className="h-full rounded-full bg-ds-ink" style={{ width: `${Math.round(share(analyzed, analyzable) * 100)}%` }} />
            </div>
          ) : null}
          <TopAds ads={analysis.topAds} />
        </div>
      );
    case 'done':
      return (
        <>
          {analyzable > 0 ? (
            <p className="text-label text-ds-text-3">{`${analyzed} of ${analyzable} ads analyzed`}</p>
          ) : null}
          <Findings analysis={analysis} />
          <TopAds ads={analysis.topAds} />
        </>
      );
    case 'partial':
      return (
        <>
          <Note tone="warn">{partialAnalysisNote(analysis.errorCode, analyzed, analyzable)}</Note>
          <Findings analysis={analysis} />
          <TopAds ads={analysis.topAds} />
        </>
      );
    case 'unavailable':
      return (
        <>
          <Note tone="warn">{unavailableAnalysisNote(analysis.errorCode)}</Note>
          <TopAds ads={analysis.topAds} />
        </>
      );
  }
}

export interface AdAnalysisBlockProps {
  analysis: DataState<AdAnalysisSummary>;
}

/**
 * Scout's "Ad analysis" block of the ads card: marketing-core's brand ad profile of the target account, in the
 * state the job is in. Nothing is scored or rated, and a number the profile did not send is not drawn. Before
 * the first read (`idle` / `loading`) the block is absent rather than guessing.
 */
export function AdAnalysisBlock({ analysis }: AdAnalysisBlockProps) {
  if (analysis.state === 'idle' || analysis.state === 'loading') return null;
  const state = analysis.state === 'error' ? 'unavailable' : analysis.data.state;
  return (
    <section data-testid="ad-analysis" data-state={state} aria-label={AD_ANALYSIS.title} className="flex flex-col gap-1 border-t border-ds-hairline pt-3 text-body-sm">
      <h4 className="text-tab text-foreground">{AD_ANALYSIS.title}</h4>
      {analysis.state === 'error' ? <Note tone="warn">{AD_ANALYSIS.error}</Note> : <Body analysis={analysis.data} />}
    </section>
  );
}
